import { Cell } from 'react-table';
import ProgressBar from '@ramonak/react-progress-bar';

import { theme } from '../../tailwind.theme';
import {
  BodyRegular,
  BodyRegularBold,
  BodySmallRegularBold,
  CaptionBold,
  CaptionRegular
} from '@components/atoms/typography';
import Icon from '@components/atoms/icon';
import PlayerFaceWithShirtFallback from '@components/player/face-with-shirt-fallback';
import PlayerAvailabilityBadge from '@components/player-availability-badge';
import Tooltip from '@components/atoms/tooltip';
import type { Player } from '@api-service/players';

const POSITION_SHORT_LABELS: Record<Player['position'], string> = {
  GK: 'GK',
  DEF: 'DEF',
  MID: 'MID',
  FWD: 'FWD'
};

const CHANGE_TIME_SORT_VALUES: Record<string, number> = {
  Tonight: 1,
  Tomorrow: 2,
  '2 Days': 3,
  '1 Week': 4,
  '>1 Week': 5,
  Locked: 6
};

const changeTimeSortValue = (text: string): number =>
  CHANGE_TIME_SORT_VALUES[text] ?? CHANGE_TIME_SORT_VALUES.Locked;

const PriceRisePlayerCell = ({
  player,
  isMobile
}: {
  player: Player;
  isMobile: boolean;
}) => (
  <div
    data-cy={`price-rise-player-position-${player.position}`}
    className={`z-50 bg-white ${isMobile ? 'max-w-[100px]' : ''}`}
  >
    <div className="flex items-center mb-1">
      <div className="h-8 w-8 mr-2 flex items-center justify-center">
        <PlayerFaceWithShirtFallback player={player} />
      </div>
      <BodyRegularBold
        className={`mb-0 ${isMobile ? 'truncate ... w-[80px]' : ''}`}
        data-cy={`price-rise-player-${player.displayName}`}
      >
        {player.displayName}
      </BodyRegularBold>
    </div>
    <div className="flex items-center">
      <CaptionBold textColor="text-neutral-600">
        {player.team.shortName}
      </CaptionBold>
      <div className="w-1 h-1 bg-neutral-400 mx-1 rounded" />
      <CaptionRegular>{POSITION_SHORT_LABELS[player.position]}</CaptionRegular>
      {/* Inline because the 32px face is too small to carry it. */}
      <PlayerAvailabilityBadge
        status={player.status}
        chanceOfPlaying={player.chanceOfPlaying}
        placement="inline"
        className="ml-1.5"
      />
    </div>
  </div>
);

export enum PriceRiseColumnAccessors {
  Player = 'player',
  Price = 'price',
  Progress = 'progress',
  Direction = 'direction',
  ChangeTime = 'changeTime',
  HourlyRate = 'hourlyRate'
}

const compareNumeric = (rowA, rowB, id, desc) => {
  // NaN values are clamped to the same +/-Infinity so the comparator
  // stays commutative (compare(a,b) === -compare(b,a)). Returning a fixed
  // sign on the first NaN check would break that contract and produce
  // unstable orderings in react-table.
  const nanReplacement = desc
    ? Number.NEGATIVE_INFINITY
    : Number.POSITIVE_INFINITY;
  const rawA = Number(rowA.values[id]);
  const rawB = Number(rowB.values[id]);
  const a = Number.isNaN(rawA) ? nanReplacement : rawA;
  const b = Number.isNaN(rawB) ? nanReplacement : rawB;
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

const compareChangeTime = (rowA, rowB, id) => {
  const a = rowA.values[id]?.sortValue ?? 0;
  const b = rowB.values[id]?.sortValue ?? 0;
  const secondaryA = Number(rowA.values.progress);
  const secondaryB = Number(rowB.values.progress);
  if (a === b) {
    if (secondaryA < 0 && secondaryB < 0) {
      return secondaryA > secondaryB ? 1 : -1;
    }
    return secondaryA < secondaryB ? 1 : -1;
  }
  return a < b ? -1 : 1;
};

export type PriceRiseRow = {
  player: Player;
  price: string;
  progress: number;
  direction: number;
  changeTime: { text: string; sortValue: number };
  hourlyRate: number;
};

export const toPriceRiseRow = (player: Player): PriceRiseRow => {
  const progress = player.priceChange?.progress ?? 0;
  const changeTimeText = player.priceChange?.estimatedChangeTime ?? 'Locked';
  return {
    player,
    price: `£${player.price.toFixed(1)}m`,
    progress,
    direction: progress === 0 ? 0 : progress > 0 ? 1 : -1,
    changeTime: {
      text: changeTimeText,
      sortValue: changeTimeSortValue(changeTimeText)
    },
    hourlyRate: player.priceChange?.hourlyRate ?? 0
  };
};

export const generatePriceRiseColumns = ({
  isMobile
}: {
  isMobile: boolean;
}) => {
  const changeTimeColumn = {
    Header: () => (
      <div className="flex items-center">
        <BodySmallRegularBold textColor="text-neutral-600">
          Change time
        </BodySmallRegularBold>
        <div className="hidden sm:flex">
          <Tooltip
            variant="light"
            title="This estimates when we believe the price will change."
          >
            <span className="ml-1">
              <Icon className="w-4 h-4" name="InfoFilled" />
            </span>
          </Tooltip>
        </div>
      </div>
    ),
    accessor: PriceRiseColumnAccessors.ChangeTime,
    disableFilters: true,
    sortType: compareChangeTime,
    minWidth: 100,
    Cell: (cell: Cell) => (
      <div className="flex items-center min-w-[100px]">
        {cell.value?.text === 'Tonight' && (
          <Icon className="text-warning-600 mr-2" name="Warning" />
        )}
        <BodyRegular
          textColor={
            cell.value?.text === 'Tonight'
              ? 'text-warning-600'
              : 'text-neutral-800'
          }
        >
          {cell.value?.text}
        </BodyRegular>
      </div>
    )
  };

  return [
    {
      Header: 'Player',
      accessor: PriceRiseColumnAccessors.Player,
      disableSortBy: true,
      disableFilters: true,
      sticky: 'left',
      Cell: ({ value }: Cell) => (
        <PriceRisePlayerCell player={value} isMobile={isMobile} />
      )
    },
    ...(isMobile ? [changeTimeColumn] : []),
    {
      Header: () => (
        <div className="flex items-center">
          <BodySmallRegularBold textColor="text-neutral-600">
            Progress
          </BodySmallRegularBold>
          <div className="hidden sm:flex">
            <Tooltip
              variant="light"
              title="The player's price is predicted to increase once it hits 100% and is predicted to decrease once it hits -100%."
            >
              <span className="ml-1">
                <Icon className="w-4 h-4" name="InfoFilled" />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
      accessor: PriceRiseColumnAccessors.Progress,
      disableFilters: true,
      maxWidth: 150,
      sortType: compareNumeric,
      Cell: (cell: Cell) => {
        const progress = Number(cell.value);
        const absProgress = Math.abs(progress);
        return (
          <div className={`${isMobile ? 'w-[80px]' : 'w-[100px]'}`}>
            <ProgressBar
              bgColor={
                progress < 0
                  ? theme.colors.danger[100]
                  : theme.colors.primary[100]
              }
              borderRadius="0px"
              height="22px"
              completed={absProgress}
              className="mt-2"
              dir={progress < 0 ? 'rtl' : 'ltr'}
              barContainerClassName="text-center bg-neutral-100 relative rounded-lg"
              customLabel={`${absProgress.toFixed(0)}%${
                progress < 0 ? '-' : ''
              }`}
              labelClassName={`text-neutral-800 font-sans font-normal text-xxs absolute ${
                isMobile ? 'top-[4px] left-[25px]' : 'top-[4px] left-[36px]'
              } `}
            />
          </div>
        );
      }
    },
    {
      Header: 'Price',
      accessor: PriceRiseColumnAccessors.Price,
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: () => (
        <div className="flex items-center">
          <BodySmallRegularBold textColor="text-neutral-600">
            Price prediction
          </BodySmallRegularBold>
          <div className="hidden sm:flex">
            <Tooltip
              variant="light"
              title="A price increase/decrease will change the player's value by £0.1m"
            >
              <span className="ml-1">
                <Icon className="w-4 h-4" name="InfoFilled" />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
      accessor: PriceRiseColumnAccessors.Direction,
      disableFilters: true,
      Cell: (cell: Cell) => {
        const direction = Number(cell.value);
        return (
          <div className="flex items-center">
            {direction !== 0 && (
              <Icon
                className={`w-5 h-5 mr-1 ${
                  direction > 0
                    ? 'text-success-600'
                    : 'text-danger-600 rotate-90'
                }`}
                name="ArrowUpRightFilled"
              />
            )}
            <BodyRegular>
              {direction === 0 ? '-' : direction > 0 ? 'Increase' : 'Decrease'}
            </BodyRegular>
          </div>
        );
      }
    },
    {
      Header: () => (
        <div className="flex items-center">
          <BodySmallRegularBold textColor="text-neutral-600">
            Hourly change
          </BodySmallRegularBold>
          <div className="hidden sm:flex">
            <Tooltip
              variant="light"
              title="This column shows how fast the Progress column is going up or down."
            >
              <span className="ml-1">
                <Icon className="w-4 h-4" name="InfoFilled" />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
      accessor: PriceRiseColumnAccessors.HourlyRate,
      disableFilters: true,
      sortType: compareNumeric,
      Cell: (cell: Cell) => {
        const value = Number(cell.value);
        return (
          <div className="flex items-center w-[100px]">
            <BodyRegular>{value.toFixed(2)}%</BodyRegular>
            <div className="flex items-center ml-2">
              {transfersTrendGenerator(value)}
            </div>
          </div>
        );
      }
    },
    ...(!isMobile ? [changeTimeColumn] : [])
  ];
};

export const transfersTrendGenerator = (value: number) => {
  if (value > 1.5)
    return (
      <div className="flex items-center">
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img src="/images/three-chevron-up.png" alt="3 up arrow" />
        </div>
      </div>
    );
  if (value > 0.6)
    return (
      <div className="flex items-center">
        <div className="mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/two-chevron-up.png"
            alt="2 up arrow"
          />
        </div>
      </div>
    );
  if (value > 0)
    return (
      <div className="flex items-center">
        {' '}
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/one-chevron-up.png"
            alt="1 up arrow"
          />
        </div>
      </div>
    );
  if (value < -1.5)
    return (
      <div className="flex items-center">
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/three-chevron-down.png"
            alt="3 down arrow"
          />
        </div>
      </div>
    );
  if (value < -0.6)
    return (
      <div className="flex items-center">
        <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
          <img
            className="mb-0"
            src="/images/two-chevron-down.png"
            alt="2 down arrow"
          />
        </div>
      </div>
    );
  if (value === 0) return <div className="flex items-center"></div>;
  return (
    <div className="flex items-center">
      <div className="mb-0 mr-2 min-w-[16px] h-[15px] flex items-center">
        <img
          className="mb-0"
          src="/images/one-chevron-down.png"
          alt="1 down arrow"
        />
      </div>
    </div>
  );
};

export enum SeasonHistoryColumnAccessors {
  seasonName = 'seasonName',
  totalPoints = 'totalPoints',
  rank = 'rank',
  percentage = 'percentage'
}

export const seasonHistoryTableColumns = () => [
  {
    Header: 'Season',
    accessor: SeasonHistoryColumnAccessors.seasonName,
    Cell: ({ value }: Cell) =>
      value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor="text-neutral-600"
            className="text-sm md:text-base"
          >
            {value.toLocaleString()}
          </BodyRegular>
        </div>
      ) : null
  },
  {
    Header: 'Total Points',
    accessor: SeasonHistoryColumnAccessors.totalPoints,
    Cell: ({ value }: Cell) =>
      value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor="text-neutral-600"
            className="text-sm md:text-base"
          >
            {value.toLocaleString()}
          </BodyRegular>
        </div>
      ) : null
  },
  {
    Header: 'Rank',
    accessor: SeasonHistoryColumnAccessors.rank,
    Cell: ({ value }: Cell) => {
      return value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor="text-neutral-600"
            className="text-sm md:text-base"
          >
            {value.toLocaleString()}
          </BodyRegular>
        </div>
      ) : null;
    }
  },
  {
    Header: () => (
      <div className="flex items-center">
        <BodySmallRegularBold
          className="font-bold font-open-sans text-xs"
          textColor="text-neutral-600"
        >
          Percentage
        </BodySmallRegularBold>
        <div className="hidden sm:flex">
          <Tooltip
            variant="light"
            title="This is the top % you finished at for that season."
          >
            <span className="ml-1">
              <Icon className="w-4 h-4" name="InfoFilled" />
            </span>
          </Tooltip>
        </div>
      </div>
    ),
    accessor: SeasonHistoryColumnAccessors.percentage,
    Cell: ({ value }: Cell) =>
      value !== 'none' ? (
        <div className="inline-flex items-center py-1">
          <BodyRegular
            textColor={
              value < 1
                ? 'text-[#DDA42E]'
                : value < 10
                ? 'text-success-600'
                : value < 25
                ? 'text-neutral-600'
                : 'text-danger-600'
            }
            className="ml-1 p-1 rounded text-sm md:text-base"
          >
            {getSeasonPercentageEmojiMedals(value)}
          </BodyRegular>
        </div>
      ) : null
  }
];

const getSeasonPercentageEmojiMedals = (percentage) => {
  if (percentage < 0.5) {
    return `🏆 ${percentage.toFixed(2)}%`;
  }
  if (percentage < 1) {
    return `🥇 ${percentage.toFixed(2)}%`;
  }
  if (percentage < 10) {
    return `🥈 ${percentage.toFixed(2)}%`;
  }
  if (percentage < 25) {
    return `🥉 ${percentage.toFixed(2)}%`;
  }
  return `${percentage.toFixed(2)}%`;
};
